import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { environment } from '../../environments/environment';

export interface ProjectStageDto {
  projectManagementTypeId: number;
  projectManagementType: string;
  cpdepPhaseId?: number | null;
  cpdepPhase?: string | null;
  designProgressId?: number | null;
  designProgress?: string | null;
  projectPhase?: string | null;
  classOfEstimate?: string | null;
  classNumber?: string | null;
}

function normalizeName(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFKC')
    .replace(/[–—−]/g, '-')
    .replace(/\s*\/\s*/g, '/')
    .replace(/\s*:\s*/g, ': ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Stage data (CPDEP phase, design progress, class of estimate) per project management type.
 * Values come from the backend, which is the single source of truth.
 */
@Injectable({ providedIn: 'root' })
export class ProjectStageService {
  private readonly http = inject(HttpClient);
  private readonly apiBaseUrl = environment.apiBaseUrl;
  private stages: ProjectStageDto[] = [];
  private loadPromise: Promise<void> | null = null;

  ensureLoaded(): Promise<void> {
    if (!this.loadPromise) {
      this.loadPromise = firstValueFrom(this.http.get<ProjectStageDto[]>(`${this.apiBaseUrl}/api/ProjectStages`))
        .then(rows => {
          this.stages = rows ?? [];
        })
        .catch(error => {
          this.loadPromise = null;
          throw error;
        });
    }

    return this.loadPromise;
  }

  getByType(projectManagementType: string): ProjectStageDto | null {
    const normalized = normalizeName(projectManagementType ?? '');
    return this.stages.find(s => normalizeName(s.projectManagementType) === normalized) ?? null;
  }

  getCpdepPhase(projectManagementType: string): string | null {
    return this.getByType(projectManagementType)?.cpdepPhase ?? null;
  }

  getDesignProgress(projectManagementType: string): string | null {
    return this.getByType(projectManagementType)?.designProgress ?? null;
  }

  getClassNumber(projectManagementType: string): string | null {
    const stage = this.getByType(projectManagementType);
    return stage?.classNumber ?? stage?.classOfEstimate ?? null;
  }
}
